<template>
  <section
    class="merge-request-merge-area-card"
    :class="{ '--success': mergeRequest.canMerge }"
  >
    <div
      v-if="mergeRequest?.autoMergeEnabled"
      class="merge-request-merge-area-card__alert"
    >
      <i class="gf-icon gf-icon-warning"></i>
      <span>
        {{ $t('merge_disabled_auto_merge') }}
      </span>
    </div>
    <div class="merge-request-merge-area-card__card">
      <div class="merge-request-merge-area-card__header">
        <div class="title">
          <i class="gf-icon gf-icon-clipboard-check"></i>
          <h6>{{ mergeRequest?.mergeStatus?.title }}</h6>
        </div>
        <div class="actions">
          <gf-link-button
            v-if="mergeRequest.canConflictsBeResolvedInUI"
            :text="$t('resolveConflicts')"
            variant="success"
            :is-reactive="true"
            :to="{
              name: 'merge-request-conflicts'
            }"
          />
          <button
            class="btn btn-sm open-statuses"
            @click="onOpenStatusesClick"
          >
            <i :class="openStatusesIcon"></i>
          </button>
        </div>
      </div>
      <div
        v-if="areStatusesOpened"
        class="merge-request-merge-area-card__conditions"
      >
        <div
          v-for="(status, index) in mergeRequest.mergeRequestRequirementsStatuses"
          :key="index"
          class="status"
        >
          <div class="title">
            <i
              v-if="status.accepted"
              class="gf-icon gf-icon-check-circle --success"
            ></i>
            <i
              v-else
              class="gf-icon gf-icon-x-circle --danger"
            ></i>
            <span>{{ status.message }}</span>
            <button
              v-if="status.mergeRequestRequirement === 'NO_CONFLICTS' && !status.accepted"
              class="btn btn-sm open-conflicts"
              @click="areConfilcsOpened = !areConfilcsOpened"
            >
              <i
                class="gf-icon gf-fs-2"
                :class="areConflictsOpenedIcon"
              ></i>
            </button>
            <button
              v-if="status.mergeRequestRequirement === 'NO_LOCK_CONFLICTS' && !status.accepted"
              class="btn btn-sm open-conflicts"
              @click="areLockConflictsOpened = !areLockConflictsOpened"
            >
              <i
                class="gf-icon gf-fs-2"
                :class="areLockConflictsOpenedIcon"
              ></i>
            </button>
          </div>
          <div
            v-if="
              areConfilcsOpened &&
              status.mergeRequestRequirement === 'NO_CONFLICTS' &&
              !status.accepted
            "
            class="conflicts"
          >
            <div
              v-for="(fileName, index) in mergeRequest.conflicts"
              :key="index"
              class="conflict"
            >
              <span class="index">{{ index + 1 }}.</span>
              <i class="gf-icon gf-icon-file-delete gf-fs-2"></i>
              <span class="filename">{{ fileName }}</span>
            </div>
          </div>
          <div
            v-if="
              areLockConflictsOpened &&
              status.mergeRequestRequirement === 'NO_LOCK_CONFLICTS' &&
              !status.accepted
            "
            class="conflicts"
          >
            <div
              v-for="(fileName, index) in mergeRequest.lockConflicts"
              :key="index"
              class="conflict"
            >
              <span class="index">{{ index + 1 }}.</span>
              <i class="gf-icon gf-icon-file-delete gf-fs-2"></i>
              <span class="filename">{{ fileName }}</span>
              <span>{{ $t('message.locked_by') }} @{{ file?.lockedBy }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="merge-request-merge-area-card__actions">
        <div class="action">
          <merge-button
            @cancel="openCancelRequestModal"
            @close="openCloseRequestModal"
            @merge="openMergeRequestModal"
          />
        </div>
        <div class="description">
          <div
            class="merge-train"
            v-if="areMergeTrainsEnabled"
          >
            {{ $t('merge_request_cmd_train') }}
            <gf-custom-tooltip
              position="top"
              width="250px"
              :show-on-mobile-version="true"
              class="ml-1"
            >
              <template #element>
                <div class="d-flex align-items-center">
                  <i class="gf-icon gf-icon-question-circle"></i>
                </div>
              </template>
              <template #content>
                <span>{{ $t('merge_request_train_description') }}</span>
              </template>
            </gf-custom-tooltip>
          </div>
          <span>
            {{ $t('merge_request_cmd') }}
          </span>
        </div>
      </div>
    </div>
    <gf-custom-modal-window
      ref="modalCancelMerge"
      class="merge-request-merge-area-card__modal"
      gf-modal-data-name="cancelMergeModal"
      :gf-modal-header="$t('merge_request_confirm_cancel')"
      gf-modal-wrapper-width="600px"
    >
      <template #body>
        <div class="modal-actions">
          <gf-button
            variant="outline-secondary"
            :text="$t('message.cancel')"
            @click="closeCancelRequestModal"
          />
          <gf-button
            variant="primary"
            :text="$t('message.continue')"
            @click="cancelRequest"
          />
        </div>
      </template>
    </gf-custom-modal-window>
    <gf-custom-modal-window
      ref="modalCloseMerge"
      class="merge-request-merge-area-card__modal"
      gf-modal-data-name="closeMergeModal"
      :gf-modal-header="$t('merge_request_confirm_close')"
      gf-modal-wrapper-width="600px"
    >
      <template #body>
        <div class="modal-actions">
          <gf-button
            variant="outline-secondary"
            :text="$t('message.cancel')"
            @click="closeCloseRequestModal"
          />
          <gf-button
            variant="primary"
            :text="$t('message.continue')"
            @click="closeRequest"
          />
        </div>
      </template>
    </gf-custom-modal-window>
    <gf-custom-modal-window
      v-if="mergeRequest.canMerge"
      ref="modalMerge"
      class="merge-request-merge-area-card__modal"
      gf-modal-data-name="mergeModal"
      :gf-modal-header="$t('merge_request_confirm_merge')"
      gf-modal-wrapper-width="600px"
    >
      <template #body>
        <merge-request-merge-form
          ref="mergeMergeRequestForm"
          @cancel="closeMergeRequestModal"
          @submit="mergeMergeRequest"
        />
      </template>
    </gf-custom-modal-window>
  </section>
</template>

<script>
import MergeRequestMessages from '@shared/modules/merge-request/mergeRequestMessages';
import ACTION_ENUM from '@shared/modules/custom-roles/custom-roles.constants';
import MergeButton from '@shared/modules/merge-request/components/item/MergeButton';
import GfCustomModalWindow from '@shared/shared-components/shared/GfCustomModalWindow';
import MergeRequestMergeForm from '@shared/modules/merge-request/forms/item/MergeRequestMergeForm';
import MergeRequestService from '@shared/modules/merge-request/merge-request.service';
import GfButton from '@/shared-modules/shared-components/shared/components/GfButton';
import GfLinkButton from '@/shared-modules/shared-components/shared/components/GfLinkButton';
import GfCustomTooltip from '@/shared-modules/shared-components/shared/GfCustomTooltip';

export default {
  name: 'MergeRequestMergeAreaCard',
  components: {
    GfLinkButton,
    MergeButton,
    GfCustomModalWindow,
    GfButton,
    MergeRequestMergeForm,
    GfCustomTooltip
  },
  data() {
    return {
      areStatusesOpened: false,
      areConfilcsOpened: false,
      areLockConflictsOpened: false
    };
  },
  computed: {
    mergeRequest() {
      return this.$store.getters.getSelectedMergeRequest;
    },
    openStatusesIcon() {
      return this.areStatusesOpened ? 'gf-icon-chevron-up' : 'gf-icon-chevron-down';
    },
    mergeRequestId() {
      return this.$route.params?.id;
    },
    isMergeDisabled() {
      return (
        !this.mergeRequest.canMerge ||
        this.mergeRequest?.autoMergeEnabled ||
        !this.getActionAvailabilityFlag(ACTION_ENUM.DO_MERGE_MERGE_REQUEST)
      );
    },
    areConflictsOpenedIcon() {
      return this.areConfilcsOpened ? 'gf-icon-chevron-up' : 'gf-icon-chevron-down';
    },
    areLockConflictsOpenedIcon() {
      return this.areConfilcsOpened ? 'gf-icon-chevron-up' : 'gf-icon-chevron-down';
    },
    ACTION_ENUM() {
      return ACTION_ENUM;
    },
    areMergeTrainsEnabled() {
      return document.getElementById('mergeTrainsEnabled')?.value === 'true';
    }
  },
  mounted() {
    this.areStatusesOpened = !this.mergeRequest.canMerge;
  },
  methods: {
    onOpenStatusesClick() {
      this.areStatusesOpened = !this.areStatusesOpened;
    },
    getActionAvailabilityFlag(action) {
      return this.$store.getters.getActionAvailabilityFlag(action);
    },
    openCancelRequestModal() {
      this.$refs.modalCancelMerge.openModalWindow();
    },
    closeCancelRequestModal() {
      this.$refs.modalCancelMerge.closeModalWindow();
    },
    cancelRequest() {
      this.$emit('cancel');
      this.closeCancelRequestModal();
    },
    openCloseRequestModal() {
      this.$refs.modalCloseMerge.openModalWindow();
    },
    closeCloseRequestModal() {
      this.$refs.modalCloseMerge.closeModalWindow();
    },
    closeRequest() {
      this.$emit('close');
      this.closeCloseRequestModal();
    },
    openMergeRequestModal() {
      this.$refs.modalMerge.openModalWindow();
    },
    closeMergeRequestModal() {
      this.$refs.modalMerge.closeModalWindow();
    },
    mergeMergeRequest(message) {
      MergeRequestService.sendEditMergeCommitMessage(
        this.$route.params.alias,
        this.$route.params.projectAlias,
        this.$route.params.id,
        {
          commitMessage: message.mergeMessage,
          squashCommitMessage: this.mergeRequest?.squashCommit ? message.squashMessage : ''
        }
      )
        .then((res) => {
          if (res.status === 200) {
            window.location.href = `/project/${this.$route.params.alias}/${this.$route.params.projectAlias}/merge-request/${this.$route.params.id}`;
          }
        })
        .catch(() => {
          this.closeMergeRequestModal();
          this.$refs.mergeMergeRequestForm.enableSubmit();
        });
    }
  },
  i18n: {
    messages: MergeRequestMessages
  }
};
</script>

<style lang="scss">
$this: '.merge-request-merge-area-card';

#{$this} {
  --merge-area-status-color: var(--warning);
  display: flex;
  flex-direction: column;
  gap: 12px;

  &.--success {
    --merge-area-status-color: var(--success);
  }

  #{$this}__alert {
    display: flex;
    align-items: flex-start !important;
    gap: 4px;
    color: var(--danger);
  }

  #{$this}__card {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border: 1px solid var(--merge-area-status-color);
    border-radius: 6px;

    #{$this}__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;

      & > .title {
        & > i {
          margin-right: 4px;
          color: var(--merge-area-status-color);
        }
        & > h6 {
          margin-bottom: 0;
        }
      }

      & > .actions {
        display: flex;
        align-items: center;
        gap: 16px;

        & > .open-statuses {
          min-height: 20px;
          padding: 0;

          &:focus {
            box-shadow: none;
          }
        }
      }
    }
    #{$this}__conditions {
      margin-left: 24px;
      display: flex;
      flex-direction: column;
      gap: 8px;

      & > .status {
        display: flex;
        flex-direction: column;
        gap: 8px;

        & > .title {
          display: flex;
          align-items: center;
          gap: 4px;

          & > i {
            &.--success {
              color: var(--success);
            }
            &.--danger {
              color: var(--danger);
            }
          }

          & > .open-conflicts {
            min-height: 20px;
            margin-left: 4px;
            padding: 1px;
            border: 1px solid var(--border-color);
            color: var(--text-muted);

            &:hover {
              color: var(--text);
            }

            &:focus {
              box-shadow: none;
            }
          }
        }

        & > .conflicts {
          margin-left: 24px;
          display: flex;
          flex-direction: column;
          gap: 4px;

          & > .conflict {
            display: flex;
            gap: 2px;
            color: var(--text-muted);

            & > .index {
              margin-right: 2px;
            }

            & > .filename {
              overflow: hidden;
              text-overflow: ellipsis;
              direction: rtl;
            }
          }
        }
      }
    }
    #{$this}__actions {
      display: flex;
      align-items: center;
      gap: 8px;

      & > .action {
        margin-bottom: auto;
      }

      & > .description {
        display: flex;
        flex-direction: column;
        gap: 6px;

        & > .merge-train {
          display: flex;
        }
      }
    }
  }
  #{$this}__modal {
    & .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }
  }
}
</style>
